import type { Schemas } from "@platform/contracts/client";
import { Button, Label, Note, PageHeader, Panel, StatusBadge, statusTone, Tag, Textarea } from "@platform/ui";
import { ErrorText, errorKey, localName, useSession } from "@platform/ui/features";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useTranslations } from "use-intl";
import { ConsoleCrumb } from "../components/crumb";
import { ProductPreview } from "../components/product-preview";
import { OverdueNote } from "../components/queue";
import type { OpsCategory } from "../lib/catalog";
import { formatDateTime, locale } from "../lib/i18n";

type Detail = Schemas["OpsProductReviewDetail"];
type Decision = "approve" | "reject";

const MIN_REASON = 3;

/**
 * One product review (CAT-5, ADM-2): the listing as its public page would show it, the entries it matched, and for
 * edits the product as published; closed only by a decision with a reason, which the seller receives.
 */
export function ProductReviewPage() {
  const t = useTranslations();
  const { reviewId = "" } = useParams();
  const { session, state } = useSession();
  const me = state.status === "signed_in" ? state.claims.sub : undefined;
  // The enterprise's page belongs to verification reviewers; others are not offered a link they cannot open.
  const seesEnterprises =
    state.status === "signed_in" && state.claims.perm.includes("ops:verification.review");
  const [view, setView] = useState<Detail | undefined>();
  const [categories, setCategories] = useState<readonly OpsCategory[]>([]);
  const [decision, setDecision] = useState<Decision>("approve");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [detail, tree] = await Promise.all([
      session.api.GET("/v1/ops/product-reviews/{reviewId}", { params: { path: { reviewId } } }),
      session.api.GET("/v1/ops/categories"),
    ]);
    if (detail.data === undefined) return setError(t(errorKey(detail.error)));
    setCategories(tree.data?.items ?? []);
    setView(detail.data);
  }, [session, reviewId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const assign = async () => {
    const { data, error: problem } = await session.api.POST("/v1/ops/product-reviews/{reviewId}/assign", {
      params: { path: { reviewId } },
      body: {},
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setView((current) => (current === undefined ? current : { ...current, ...data }));
  };

  const decide = async (event: FormEvent) => {
    event.preventDefault();
    if (reason.trim().length < MIN_REASON) return setError(t("admin.case.reasonTooShort"));
    setBusy(true);
    setError(undefined);
    try {
      const { data, error: problem } = await session.api.POST("/v1/ops/product-reviews/{reviewId}/decide", {
        params: { path: { reviewId } },
        body: { decision, reason: reason.trim() },
      });
      if (data === undefined) return setError(t(errorKey(problem)));
      setView((current) => (current === undefined ? current : { ...current, ...data }));
    } finally {
      setBusy(false);
    }
  };

  if (view === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  const name = localName(view.product.name, locale);
  return (
    <section aria-labelledby="review-title" className="flex flex-col gap-5">
      <PageHeader
        id="review-title"
        breadcrumb={<ConsoleCrumb to="/product-reviews" back={t("admin.reviews.title")} current={name} />}
        title={t("admin.reviews.detailTitle", { product: name })}
        marks={
          <>
            <Tag label={t(view.revisionId === null ? "admin.reviews.newListing" : "admin.reviews.edits")} />
            <StatusBadge
              label={t(`admin.products.status.${view.product.status}`)}
              tone={statusTone("product", view.product.status)}
            />
          </>
        }
      />
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-5">
          <Panel title={t(view.revisionId === null ? "admin.reviews.listing" : "admin.reviews.proposed")}>
            <ProductPreview content={view.content} categories={categories} images={view.images} />
          </Panel>
          {view.published !== null && (
            <details className="group rounded-(--radius-card) border border-border bg-surface">
              <summary className="cursor-pointer px-5 py-4 text-subheading font-semibold text-ink">
                {t("admin.reviews.published")}
              </summary>
              <div className="border-t border-border-subtle px-5 py-4">
                <ProductPreview content={view.published} categories={categories} images={view.images} />
              </div>
            </details>
          )}
        </div>

        <aside className="flex flex-col gap-5 xl:sticky xl:top-5">
          <Panel title={t("admin.reviews.caseTitle")}>
            {view.decision === null && <OverdueNote startedAt={view.createdAt} inset={false} />}
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-small">
              <dt className="text-ink-muted">{t("admin.reviews.opened")}</dt>
              <dd className="text-ink">{formatDateTime(view.createdAt)}</dd>
              {seesEnterprises && (
                <>
                  <dt className="text-ink-muted">{t("admin.reviews.sellerLabel")}</dt>
                  <dd>
                    <Link
                      className="text-brand-600 hover:text-brand-700"
                      to={`/enterprises/${view.product.enterpriseId}`}
                    >
                      {t("admin.reviews.seller")}
                    </Link>
                  </dd>
                </>
              )}
            </dl>
            <div className="flex flex-col gap-2">
              <h3 className="text-label font-semibold text-ink-muted uppercase">
                {t("admin.reviews.matched")}
              </h3>
              {view.matchedEntries.length === 0 ? (
                <p className="text-small text-ink">{t(`admin.reviews.reasons.${view.reason}`)}</p>
              ) : (
                <Note tone="attention">
                  <ul className="flex flex-col gap-1">
                    {view.matchedEntries.map((entry) => (
                      <li key={entry.id}>
                        {`${t(`admin.risk.lists.${entry.listType}`)} · ${t(`admin.risk.matchTypes.${entry.matchType}`)}: ${entry.value}`}
                        {entry.note !== null && <span className="opacity-80">{` — ${entry.note}`}</span>}
                      </li>
                    ))}
                  </ul>
                </Note>
              )}
              {view.matchedEntryIds.length > view.matchedEntries.length && (
                <p className="text-caption text-ink-muted">{t("admin.reviews.removedEntries")}</p>
              )}
            </div>
            <div className="border-t border-border-subtle pt-4">
              {view.decision !== null ? (
                <Note tone={view.decision === "approved" ? "done" : "problem"}>
                  <p className="font-medium">
                    {t("admin.case.decided", { decision: t(`admin.reviews.decisions.${view.decision}`) })}
                  </p>
                  {view.decisionReason !== null && <p>{view.decisionReason}</p>}
                </Note>
              ) : (
                <form className="flex flex-col gap-3" onSubmit={(event) => void decide(event)}>
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-subheading font-semibold text-ink">
                      {t("admin.case.decisionTitle")}
                    </h3>
                    {view.assigneeOperatorId !== me && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => void assign()}>
                        {t("admin.case.assignToMe")}
                      </Button>
                    )}
                  </div>
                  <fieldset className="flex flex-col gap-2">
                    <legend className="sr-only">{t("admin.case.decisionTitle")}</legend>
                    {(["approve", "reject"] as const).map((option) => (
                      <label
                        key={option}
                        className="flex cursor-pointer items-center gap-2.5 rounded-(--radius-control) border border-border px-3 py-2 text-small text-ink transition-colors duration-(--duration-fast) hover:bg-subtle has-[:checked]:border-brand-600 has-[:checked]:bg-brand-50"
                      >
                        <input
                          type="radio"
                          name="decision"
                          value={option}
                          className="size-4 accent-brand-600"
                          checked={decision === option}
                          onChange={() => setDecision(option)}
                        />
                        {t(`admin.reviews.options.${option}`)}
                      </label>
                    ))}
                  </fieldset>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="review-reason">{t("admin.reviews.reason")}</Label>
                    <Textarea
                      id="review-reason"
                      rows={4}
                      value={reason}
                      maxLength={2000}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </div>
                  {error !== undefined && <ErrorText>{error}</ErrorText>}
                  <Button type="submit" disabled={busy || reason.trim().length < MIN_REASON}>
                    {t("admin.case.record")}
                  </Button>
                </form>
              )}
            </div>
          </Panel>
        </aside>
      </div>
    </section>
  );
}
